import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, PlayCircle, Lock, Clock, CheckCircle2, GraduationCap, FileDown, Sparkles } from "lucide-react";

export default function CourseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/formazione/courses/${id}`).then((r) => {
      setC(r.data);
      const first = (r.data.lessons || []).find((l) => l.video_url) || (r.data.lessons || [])[0];
      setActive(first);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [id]); // eslint-disable-line

  const markWatched = async (lid) => {
    try {
      await api.post(`/formazione/courses/${id}/progress/${lid}`);
      load(); // refresh progress
      toast.success("Lezione segnata come vista");
    } catch (e) { toast.error("Errore"); }
  };

  if (loading) return <div className="p-10 text-sm">Caricamento…</div>;
  if (!c) return <div className="p-10 text-sm">Corso non trovato.</div>;

  const watchedCount = (c.lessons || []).filter((l) => l.watched).length;
  const totalCount = (c.lessons || []).length;
  const pct = totalCount ? Math.round((100 * watchedCount) / totalCount) : 0;

  return (
    <div data-testid="course-detail-page" className="space-y-6">
      <Link to="/app/formazione" className="inline-flex items-center gap-2 text-sm text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]" data-testid="course-back">
        <ArrowLeft className="h-4 w-4" /> Torna ai corsi
      </Link>

      <header className="grid lg:grid-cols-[1fr_320px] gap-6 items-start">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--sp-text-muted)]">{c.category}</div>
          <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight mt-1">{c.title}</h1>
          {c.subtitle && <p className="text-sm text-[color:var(--sp-text-secondary)] mt-2">{c.subtitle}</p>}
          <div className="flex items-center gap-3 text-xs text-[color:var(--sp-text-muted)] mt-3">
            <span className="inline-flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> {totalCount} lezioni</span>
            <span className="font-bold text-[color:var(--sp-text-secondary)]">{c.instructor}</span>
          </div>
        </div>
        {c.unlocked && totalCount > 0 && (
          <div className="sp-card p-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="label-eyebrow">Progresso</span>
              <span className="font-black tabular-nums">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-[color:var(--sp-subtle)] overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${pct}%`, background: pct === 100 ? "#10B981" : "var(--sp-brand)" }} />
            </div>
            <div className="text-[11px] text-[color:var(--sp-text-muted)]">
              {watchedCount}/{totalCount} lezioni completate
              {pct === 100 && <span className="ml-1 inline-flex items-center gap-1 text-emerald-600 font-bold"><Sparkles className="h-3 w-3" /> Corso terminato!</span>}
            </div>
          </div>
        )}
      </header>

      {!c.unlocked && (
        <section className="sp-card p-6 bg-gradient-to-br from-amber-50 to-white border-amber-200 flex items-center gap-4 flex-wrap" data-testid="course-locked-banner">
          <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
            <Lock className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <div className="font-display font-black text-lg">Corso bloccato</div>
            <p className="text-xs text-[color:var(--sp-text-secondary)] mt-1">
              Passa al piano <strong>{c.required_tier === "pro" ? "Pro" : "Premium"}</strong> oppure acquista questo corso singolarmente
              {c.price_eur ? <> a <strong>€ {c.price_eur}</strong></> : null} per accedere a tutte le lezioni.
            </p>
          </div>
          <button className="sp-btn-primary text-sm" onClick={() => toast.info("Upgrade & acquisti in arrivo — parla con il tuo referente Salon Up")}>
            Sblocca corso
          </button>
        </section>
      )}

      {/* Layout video + lezioni */}
      <section className="grid lg:grid-cols-[1fr_360px] gap-6">
        {/* Video/lezione attiva */}
        <div className="sp-card p-4 space-y-4">
          {active?.video_url ? (
            <div className="aspect-video rounded-lg overflow-hidden bg-black">
              <iframe src={active.video_url} title={active.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen className="w-full h-full" />
            </div>
          ) : (
            <div className="aspect-video rounded-lg bg-[color:var(--sp-subtle)] flex flex-col items-center justify-center text-center p-6">
              <Lock className="h-10 w-10 text-[color:var(--sp-text-muted)]" />
              <div className="text-sm font-bold mt-3">Video bloccato</div>
              <p className="text-xs text-[color:var(--sp-text-muted)] mt-1 max-w-sm">
                {c.unlocked ? "Contenuto in preparazione." : "Sblocca il corso per accedere ai video."}
              </p>
            </div>
          )}
          {active && (
            <div>
              <h2 className="font-display font-black text-xl">{active.title}</h2>
              {active.description && <p className="text-sm text-[color:var(--sp-text-secondary)] mt-2 whitespace-pre-line">{active.description}</p>}
              <div className="flex items-center gap-3 text-xs text-[color:var(--sp-text-muted)] mt-3">
                {active.duration_min > 0 && (
                  <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {active.duration_min} min</span>
                )}
                {c.unlocked && !active.watched && (
                  <button onClick={() => markWatched(active.id)} data-testid="mark-watched"
                    className="ml-auto sp-btn-ghost text-xs inline-flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Segna come vista
                  </button>
                )}
                {active.watched && (
                  <span className="ml-auto inline-flex items-center gap-1 text-emerald-600 font-bold text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Vista
                  </span>
                )}
              </div>
              {(active.resources || []).length > 0 && (
                <div className="mt-4 pt-4 border-t border-[color:var(--sp-border)]">
                  <div className="label-eyebrow mb-2">Risorse allegate</div>
                  <div className="space-y-1">
                    {active.resources.map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noreferrer"
                        className="flex items-center gap-2 text-sm text-[color:var(--sp-brand)] hover:underline">
                        <FileDown className="h-4 w-4" /> {r.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Lezioni sidebar */}
        <aside className="sp-card p-0 overflow-hidden">
          <div className="p-4 border-b border-[color:var(--sp-border)]">
            <div className="label-eyebrow">Lezioni</div>
            <div className="text-sm font-bold mt-1">{totalCount} contenuti</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {(c.lessons || []).map((l, i) => {
              const isActive = active?.id === l.id;
              const canPlay = !!l.video_url;
              return (
                <button key={l.id} onClick={() => canPlay && setActive(l)} data-testid={`lesson-item-${l.id}`}
                  disabled={!canPlay}
                  className={`w-full text-left p-3 flex items-start gap-3 border-b border-[color:var(--sp-border)] transition-colors ${isActive ? "bg-[color:var(--sp-brand)]/10" : "hover:bg-[color:var(--sp-subtle)]"} ${!canPlay ? "opacity-60" : ""}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${l.watched ? "bg-emerald-100 text-emerald-700" : canPlay ? "bg-[color:var(--sp-brand)]/15 text-[color:var(--sp-brand)]" : "bg-[color:var(--sp-subtle)] text-[color:var(--sp-text-muted)]"}`}>
                    {l.watched ? <CheckCircle2 className="h-4 w-4" /> : canPlay ? <PlayCircle className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">{i + 1}. {l.title}</div>
                    <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-0.5">
                      {l.duration_min > 0 && `${l.duration_min} min`}
                      {l.preview && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold text-[9px] uppercase tracking-widest">Anteprima</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>
      </section>
    </div>
  );
}
